/**
 * Parse a "basic" one-column CSV of member IDs — the format the promo-group
 * drawer's Import accepts.
 *
 * Deliberately NOT an RFC 4180 parser and deliberately dependency-free:
 * member IDs are plain identifiers (no embedded commas, quotes, or
 * newlines), so escaping rules have nothing to protect here. Any separator
 * a spreadsheet export might use (newlines, commas, semicolons, tabs)
 * splits cells; surrounding quotes and whitespace are stripped; empty
 * cells and `member_id` header cells are skipped; duplicates are dropped
 * (first occurrence wins).
 *
 * A cell that still contains inner whitespace after unquoting is reported
 * as malformed rather than imported — it's the clearest signal the file
 * isn't actually a one-column ID list (e.g. someone exported names too).
 *
 * @param {string} text raw file contents
 * @returns {{ids: string[], invalid: string[]}} unique normalized IDs in
 *   file order, plus the cells dropped as malformed.
 */
export function parseMemberIdCsv(text) {
    const ids = [];
    const invalid = [];
    const seen = new Set();

    for (const rawCell of String(text || '').split(/[\r\n,;\t]+/)) {
        const cell = rawCell
            .trim()
            .replace(/^["']+|["']+$/g, '')
            .trim();
        if (!cell) continue;
        if (cell.toLowerCase() === 'member_id') continue;
        if (/\s/.test(cell)) {
            invalid.push(cell);
            continue;
        }
        if (seen.has(cell)) continue;
        seen.add(cell);
        ids.push(cell);
    }

    return { ids, invalid };
}
